/*
 * -----------------------------------------------------------------------
 *  <copyright file="event-log/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2021 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import moment from 'moment';
import { stringDateWithoutTimeFromLocal } from '../../../../utils/convertor';
import EventLogGrid from './grid';
import { useAuthorization, useLocalState, useTranslation } from 'hooks';
import { SEARCH_EVENT_LOGS } from 'queries/event';
import {
    UserFields,
    OrganisationMemberFields,
    OrganisationFields,
    EventRecordFields,
} from 'queries/generated/fragments.generated';
import { GET_ORGANISATION_MEMBERS_ID_EMAIL, GET_ALL_USERS_ID_EMAIL_SYSADMINLEVEL } from 'queries/uam';
import { UserLevel } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { withoutTime } from 'utils/date-helpers';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';
import EventLogSearch from 'views/pages/admin/event-log/search';

interface EventLogInterface {
    userLevel: UserLevel;
}

export interface SearchEventLogParams {
    startDate: Date;
    endDate: Date;
    userEmail: string;
    actionedByEmail: string;
}

export type SearchEventLogsFunction = (search: SearchEventLogParams) => void;

const EventLog = ({ userLevel }: EventLogInterface) => {
    const { t } = useTranslation();
    const user = useAuthorization();

    const { state: localState } = useLocalState();
    const [eventLogs, setEventLogs] = useState([] as EventRecordFields[]);

    const [searchLogs, { error: errorEventLogs, loading: loadingEventLogs }] = useLazyQuery(SEARCH_EVENT_LOGS);

    const [getOrgMembers, { data: dataOrgMembers, error: errorOrgMembers, loading: loadingOrgMembers }] = useLazyQuery(
        GET_ORGANISATION_MEMBERS_ID_EMAIL,
    );
    const {
        data: dataAllUsers,
        error: errorAllUsers,
        loading: loadingAllUsers,
    } = useQuery(GET_ALL_USERS_ID_EMAIL_SYSADMINLEVEL);

    const organisation: OrganisationFields =
        localState?.selectedOrganisation || localState?.selectedStudy?.organisation;
    const loading = loadingEventLogs || loadingOrgMembers || loadingAllUsers;
    const allUsers = dataAllUsers?.users;
    const orgMembers = dataOrgMembers?.organisation?.members;

    const defaultSearchParams: SearchEventLogParams = {
        startDate: moment().subtract(7, 'day').toDate(),
        endDate: moment().toDate(),
        userEmail: '',
        actionedByEmail: '',
    };

    const [searchParams, setSearchParams] = useState({ ...defaultSearchParams } as SearchEventLogParams);

    const resetSearchParams = () => setSearchParams({ ...defaultSearchParams });

    const getAffectedUserIds = (userEmail: string): string[] => {
        if (!userEmail) return [];

        // system event records: affected user is always system user
        // org event records: affected user is always org user (or org member)
        const users =
            userLevel === UserLevel.SYSTEM
                ? allUsers?.filter((u: UserFields) => u.email.includes(userEmail) && u.systemAdminLevel > 0)
                : allUsers?.filter((u: UserFields) => u.email.includes(userEmail) && u.systemAdminLevel < 1);

        if (users.length === 0) return undefined;

        return users.map((u: UserFields) => u.id);
    };

    const getAffectedOrganisationMemberIds = (userEmail: string, affectedUserIds: string[]): string[] => {
        if (!userEmail) return [];

        if (userLevel === UserLevel.SYSTEM) return undefined;

        // if there is a user associated with member, member email might be out of date
        // (because if the user changes their email we don't update the org member)
        // So use the userId to find member instead
        // otherwise use the email
        const members = affectedUserIds
            ? orgMembers?.filter((m: OrganisationMemberFields) => affectedUserIds.includes(m.userId))
            : orgMembers?.filter((m: OrganisationMemberFields) => m.email.includes(userEmail));

        if (members.length === 0) return undefined;

        return members.map((u: UserFields) => u.id);
    };

    const getUserIds = (actionedByEmail?: string): string[] => {
        if (!actionedByEmail) return [];

        // system event records: actioned by user is always system user
        // org event records: actioned by user may be system or org
        const users =
            userLevel === UserLevel.SYSTEM
                ? allUsers?.filter((u: UserFields) => u.email.includes(actionedByEmail) && u.systemAdminLevel > 0)
                : allUsers?.filter((u: UserFields) => u.email.includes(actionedByEmail));

        if (users.length === 0) return undefined;

        return users.map((u: UserFields) => u.id);
    };

    const doSearch: SearchEventLogsFunction = (search: SearchEventLogParams) => {
        const userIds = getUserIds(search.actionedByEmail);

        if (!userIds) {
            setEventLogs([]);
            return;
        }

        const affectedUserIds = getAffectedUserIds(search.userEmail);
        const affectedOrganisationMemberIds = getAffectedOrganisationMemberIds(search.userEmail, affectedUserIds);

        if (!affectedUserIds && !affectedOrganisationMemberIds) {
            setEventLogs([]);
            return;
        }

        let start = stringDateWithoutTimeFromLocal(search.startDate);
        if (start === null) {
            start = stringDateWithoutTimeFromLocal(moment().subtract(7, 'day').toDate());
        }
        let end = stringDateWithoutTimeFromLocal(search.endDate);
        if (end === null) {
            end = stringDateWithoutTimeFromLocal(moment().toDate());
        }

        const asyncSearch = async () => {
            const result = await searchLogs({
                variables: {
                    startDate: start,
                    endDate: end,
                    userIds,
                    affectedUserIds: affectedUserIds || [],
                    affectedOrganisationMemberIds: affectedOrganisationMemberIds || [],
                    userLevel,
                },
            });

            const data = result?.data as any;
            setEventLogs(data.eventLogs);
        };
        asyncSearch();
    };

    const doDefaultSearch = () => doSearch(defaultSearchParams);

    const clearSearch = () => {
        resetSearchParams();
        doDefaultSearch();
    };

    const getOrgMembersForOrgAdmin = () => {
        if (userLevel === UserLevel.ORGANISATION && !user.isSystemAdmin()) {
            getOrgMembers({ variables: { key: Constants.ObjectId.Empty } });
        }
    };

    const getOrgMembersForSysAdmin = () => {
        if (userLevel === UserLevel.ORGANISATION && user.isSystemAdmin()) {
            getOrgMembers({ variables: { key: organisation?.organisationKey } });
        }
    };

    useEffect(() => {
        getOrgMembersForOrgAdmin();
    }, [user]);

    useEffect(() => {
        getOrgMembersForSysAdmin();
        clearSearch();
    }, [organisation, user]);

    if (errorEventLogs) return <Error error={errorEventLogs} />;

    if (errorOrgMembers) return <Error error={errorOrgMembers} />;

    if (errorAllUsers) return <Error error={errorAllUsers} />;

    return (
        <>
            <PageTitle text={t('eventLog:eventLog')} />
            <EventLogSearch
                onSearch={doSearch}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                clearSearch={clearSearch}
            />
            {errorEventLogs && <div>Error: {errorEventLogs.message}</div>}
            {errorOrgMembers && <div>Error: {errorOrgMembers.message}</div>}
            {errorAllUsers && <div>Error: {errorAllUsers.message}</div>}
            <br />
            <br />
            {loading && <Loading float />}
            <EventLogGrid
                eventRecords={eventLogs}
                organisationMembers={orgMembers}
                allUsers={allUsers}
                userLevel={userLevel}
                organisationName={organisation?.name}
            />
        </>
    );
};

export default React.memo(EventLog);
